import { Component, Inject, OnInit } from '@angular/core';

// services
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@app/shared/services/auth.service';
import { ProfileSettingsService } from '@app/profile-settings/components/profile-settings.service';
import { LanguageService } from '@app/translation/services/language.service';
import { UserStaticSettingsService } from '@app/profile-settings/user-static-settings.service';
import { WindowToken } from '@app/shared/window.token';

// types
import { Options, Messages } from '@app/profile-settings/communications.api';
import { ActivatedRoute, Router } from '@angular/router';
import { LanguageLocale } from '@app/inputs/inputs';

import { SettingsCommunicationsComponent } from '@app/profile-settings/components/settings-communications/settings-communications.component';
import { Integrations } from '@app/profile-settings/components/integrations/integrations.component';
import { SettingsAccount } from '@app/profile-settings/components/account/settings-account.component';
import { SettingsProfile } from '@app/profile-settings/components/profile/settings-profile.component';
import { SettingsExtensionsComponent } from '@app/profile-settings/components/extensions/settings-extensions.component';
import { SettingsFamilyViewComponent } from '@app/user/components/settings/family/settings-family-view/settings-family-view.component';
import { forkJoin } from 'rxjs';
import { WindowLayoutService } from '@app/shared/services/window-layout/window-layout.service';
import { IntegrationsService } from '@app/profile-settings/components/integrations/integrations.service';
import { LDClientSDK } from '@app/shared/services/ldclientsdk.token';
import { LDClient } from 'launchdarkly-js-client-sdk';
import { StaticSettings } from '@app/profile-settings/profile-settings.api';
import { TrackerService } from '@dg/shared-services';

@Component({
  selector: 'dgx-profile-settings',
  templateUrl: './profile-settings.component.html',
})
export class ProfileSettings implements OnInit {
  public readonly CLASS_NAME = 'ProfileSettings';
  public personalSettings: StaticSettings;
  public messages: Messages;
  public options: Options;
  public orgs: any[];
  public allLanguages: LanguageLocale[];
  public systemLanguages: LanguageLocale[];

  public isMobile: boolean;
  public isLoading: boolean;
  public i18n = this.translateService.instant(['Core_Add']);

  public authUser = this.authService.authUser;
  public hasOrg = !!this.authUser.defaultOrgInfo;
  public privacyLockedMessage = '';
  public profilePrivacySettingsAreManaged = false;

  public hideIntegrations = false;
  public hasSocialIntegrations = false;
  public isClientProvider = false;
  public showFamily = false;
  private readonly familyFeatureFlag = 'degreed-family-08122020';

  constructor(
    private profileSettingsService: ProfileSettingsService,
    private translateService: TranslateService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private userStaticSettingsService: UserStaticSettingsService,
    private languageService: LanguageService,
    private windowLayoutService: WindowLayoutService,
    private integrationsService: IntegrationsService,
    private trackerService: TrackerService,
    @Inject(LDClientSDK) private ldClientSDK: LDClient
  ) {}

  public ngOnInit() {
    this.trackProfileSettingsViewed();
    this.isLoading = true;
    this.isMobile = this.windowLayoutService.isMobile;

    forkJoin({
      personalSettings: this.userStaticSettingsService.getPersonalSettings(),
      profileSettings: this.profileSettingsService.getProfileSettings(),
      allLanguages: this.languageService.getAllLanguages(),
      systemLanguages: this.languageService.getSystemLanguages(),
      integrations: this.integrationsService.getIntegrations(),
    }).subscribe(
      ({
        personalSettings,
        profileSettings,
        allLanguages,
        systemLanguages,
      }) => {
        const socialIntegrations =
          this.integrationsService.getSocialIntegrations();
        if (socialIntegrations?.availableSocialIntegrations?.length > 0) {
          this.hasSocialIntegrations = true;
        }
        this.isClientProvider =
          this.authUser.defaultOrgInfo?.settings?.isClientProvider;
        this.showFamily = this.ldClientSDK.variation(
          this.familyFeatureFlag,
          false
        );
        this.personalSettings = personalSettings;
        // fix up these interfaces - this was just to get things going
        this.messages = (profileSettings as any).messages;
        this.options = (profileSettings as any).options;
        this.orgs = (profileSettings as any).orgs || [];
        this.allLanguages = allLanguages;
        this.systemLanguages = systemLanguages;

        const { isRestrictedProfile, preventPublicProfiles } = this.options;
        const { defaultOrgInfo } = this.authUser;

        const lockProfilePrivacy = defaultOrgInfo?.settings?.lockProfilePrivacy;
        this.hideIntegrations =
          defaultOrgInfo?.settings?.hidePersonalIntegrationsInUserProfileSettings;
        if (this.hasOrg) {
          this.privacyLockedMessage = this.translateService.instant(
            'dgUserProfile_PrivacyLockedMessage',
            {
              OrgName: defaultOrgInfo.name,
            }
          );
        }

        // Displays when "Prevent Public Profiles" or "Lock Profile Privacy" are enabled in ops tool
        this.profilePrivacySettingsAreManaged =
          lockProfilePrivacy || isRestrictedProfile || preventPublicProfiles;
        this.isLoading = false;
      }
    );
  }

  public onActivate(component: any) {
    switch (component.CLASS_NAME) {
      case 'SettingsProfile':
        component = component as SettingsProfile;
        component.options = this.options;
        component.personalSettings = this.personalSettings;
        component.messages = this.messages;
        break;
      case 'SettingsAccount':
        component = component as SettingsAccount;
        component.options = this.options;
        component.personalSettings = this.personalSettings;
        component.orgs = this.orgs;
        component.languages = this.allLanguages;
        component.systemLanguages = this.systemLanguages;
        break;
      case 'SettingsCommunications':
        component = component as SettingsCommunicationsComponent;
        component.options = this.options;
        component.personalSettings = this.personalSettings;
        break;
      case 'SettingsIntegrations':
        component = component as Integrations;
        break;
      case 'SettingsExtensions':
        component = component as SettingsExtensionsComponent;
        break;
      case 'SettingsFamily':
        component = component as SettingsFamilyViewComponent;
        break;
      default:
        break;
    }
  }

  private trackProfileSettingsViewed() {
    //
    this.trackerService.trackEventData({
      action: 'Profile Settings Viewed',
    });
  }
}
